import {Component, OnInit, HostListener, ChangeDetectionStrategy} from "@angular/core";
import {NgForm} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {NzMessageService} from "ng-zorro-antd/message";
import {lastValueFrom} from "rxjs";
import {PerformaModulesService} from "src/app/services/performa-modules.service";
import {
    GroupDepartmentDto,
    GroupDocumentTypeDto,
    GroupDto,
    GroupFormDto,
    GroupLocationDto,
    GroupPermissionsDto,
    GroupStepDto,
    GroupUserDto,
    ModuleDto,
    PerFormaAPIService
} from "src/app/services/pfapi.service";

@Component({
    selector: "app-group-edit",
    templateUrl: "./group-edit.component.html",
    styleUrl: "./group-edit.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GroupEditComponent implements OnInit {
    constructor(
        private readonly api: PerFormaAPIService,
        private route: ActivatedRoute,
        private router: Router,
        private message: NzMessageService,
        private ModulesService: PerformaModulesService
    ) {}

    loading = true;
    isEditMode = false;

    // the following object is used to check
    // if any unsaved changes are present when
    // the user attempts to navigate away
    originalGroup: GroupDto | null = null;

    //#region Group Details

    apCheckEnabled = false;
    ptpEnabled = false;

    //#endregion

    //#region Document Type Privileges

    selectedDocType = "";
    selectableDocTypes: {label: string; value: string}[] = [];
    groupDocTypes: GroupDocumentTypeDto[] = [];

    docTypeColumns = [
        {
            title: "Document Type"
        },
        {
            title: "Upload File"
        },
        {
            title: "Edit Keyword Data"
        },
        {
            title: "Add Notes"
        },
        {
            title: "Delete Documents"
        },
        {
            title: "View Scans"
        },
        {
            title: "Delete Scans"
        },
        {
            title: ""
        }
    ];

    //#endregion

    //#region Assigned Users

    selectedUser = "";
    selectableUsers: {label: string; value: string}[] = [];
    groupUsers: GroupUserDto[] = [];

    userColumns = [
        {
            title: "Username"
        },
        {
            title: "Email"
        },
        {
            title: ""
        }
    ];

    //#endregion

    //#region Assigned Locations

    selectedLocation = "";
    selectableLocations: {label: string; value: string}[] = [];
    groupLocations: GroupLocationDto[] = [];

    locationColumns = [
        {
            title: "Location Name"
        },
        {
            title: ""
        }
    ];

    //#endregion

    //#region Assigned Departments

    selectedDepartment = "";
    selectableDepartments: {label: string; value: string}[] = [];
    groupDepartments: GroupDepartmentDto[] = [];

    departmentColumns = [
        {
            title: "Department Name"
        },
        {
            title: ""
        }
    ];

    //#endregion

    //#region Assigned Steps

    selectedStep = "";
    selectableSteps: {label: string; value: string}[] = [];
    groupSteps: GroupStepDto[] = [];

    stepColumns = [
        {
            title: "Step Name"
        },
        {
            title: "Step Type"
        },
        {
            title: "Read-only"
        },
        {
            title: ""
        }
    ];

    //#endregion

    //#region Assigned Forms

    selectedPtpForm = "";
    selectablePtpForms: {label: string; value: string}[] = [];
    groupPtpForms: GroupFormDto[] = [];

    ptpFormColumns = [
        {
            title: "Form Name"
        },
        {
            title: "Form Type"
        },
        {
            title: "Read-only"
        },
        {
            title: ""
        }
    ];

    //#endregion

    modules: ModuleDto[] = [];

    // default group object. replaced if this is an edit
    group: GroupDto = {
        GroupID: null,
        GroupName: "",
        Permissions: {
            PerFormaAdmin: false,
            PerFormaScanAdmin: false,
            APCheckAdmin: false,
            APCheckUser: false,
            ARReportUser: false,
            FINReportUser: false,
            PTPSystemCEO: false,
            PTPStoreManager: false
        } as GroupPermissionsDto,
        DocumentTypes: [],
        Users: [],
        Locations: [],
        Departments: [],
        Steps: [],
        Forms: []
    };

    async ngOnInit() {
        this.route.queryParams.subscribe(p => {
            if (p.created === "true") {
                this.message.success("Created group");
            }
        });

        // get a list of Performa modules to determine which columns to show
        this.modules = (await this.ModulesService.Modules()) ?? [];

        await this.checkEditMode();
    }

    checkEditMode = async () => {
        const id = this.route.snapshot.queryParamMap.get("id");

        if (!id) {
            this.loading = false;
            return;
        }

        this.isEditMode = true;

        try {
            this.group = await lastValueFrom(this.api.getApiConfigureGroupsId(Number(id)));
            this.originalGroup = JSON.parse(JSON.stringify(this.group));
            this.doSetup();
        } catch (e) {
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                errors.forEach(e => this.message.error(e));
            }
        } finally {
            this.loading = false;
        }
    };

    doSetup = () => {
        this.initializeAPCheck();
        this.initializePTP();
        this.setDocTypes();
        this.setUsers();
        this.setLocations();
        this.setDepartments();
        this.setSteps();
        this.setPTPForms();
    };

    save = async (frm: NgForm) => {
        frm.control.markAllAsTouched();

        if (!frm.valid) return;

        this.loading = true;

        try {
            if (this.isEditMode) {
                const resp = await lastValueFrom(this.api.putApiConfigureGroups(this.group));
                this.group = resp;
                this.message.success("Saved changes");
            } else {
                const resp = await lastValueFrom(this.api.postApiConfigureGroups(this.group));
                this.group = resp;
                this.isEditMode = true;
                this.router.navigateByUrl(`/configure/groupedit?id=${resp.GroupID}&created=true`);
            }

            this.updateOriginalData();
            this.doSetup();
            frm.control.markAsUntouched();
        } catch (e) {
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                errors.forEach(e => this.message.error(e));
            }
        } finally {
            this.loading = false;
        }
    };

    remove = async () => {
        if (!confirm("Are you sure you want to delete this group?")) {
            return;
        }
        try {
            this.loading = true;
            await lastValueFrom(this.api.deleteApiConfigureGroupsId(Number(this.group.GroupID)));
            this.router.navigateByUrl("/configure/groups");
        } catch (e) {
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                errors.forEach(e => this.message.error(e));
            }
        } finally {
            this.loading = false;
        }
    };

    //#region AP Check

    initializeAPCheck = () => {
        this.apCheckEnabled = this.group.Permissions.APCheckAdmin || this.group.Permissions.APCheckUser;

        if (this.apCheckEnabled && !this.group.Permissions.APCheckAdmin && !this.group.Permissions.APCheckUser) {
            this.group.Permissions.APCheckUser = true;
        }
    };

    onApCheckEnabledChange = (enabled: boolean) => {
        if (!enabled) {
            this.group.Permissions.APCheckAdmin = false;
            this.group.Permissions.APCheckUser = false;
        } else {
            if (!this.group.Permissions.APCheckAdmin && !this.group.Permissions.APCheckUser) {
                this.group.Permissions.APCheckUser = true;
            }
        }
    };

    //#endregion

    //#region Purchase To Pay

    initializePTP = () => {
        this.ptpEnabled = this.group.Permissions.PTPStoreManager || this.group.Permissions.PTPSystemCEO;

        if (this.ptpEnabled && !this.group.Permissions.PTPSystemCEO && !this.group.Permissions.PTPStoreManager) {
            this.group.Permissions.PTPStoreManager = true;
        }
    };

    onPTPEnabledChange = (enabled: boolean) => {
        if (!enabled) {
            this.group.Permissions.PTPStoreManager = false;
            this.group.Permissions.PTPSystemCEO = false;
        } else {
            if (!this.group.Permissions.PTPSystemCEO && !this.group.Permissions.PTPStoreManager) {
                this.group.Permissions.PTPStoreManager = true;
            }
        }
    };

    //#endregion

    //#region Document Type Privileges

    addDocType = (id: string) => {
        const item = this.group.DocumentTypes.find(t => t.DocumentTypeID === Number(id) && !t.DocumentPermissions);

        if (item) {
            item.DocumentPermissions = {
                UploadFile: false,
                EditKeywordData: false,
                AddNotes: false,
                DeleteDocuments: false,
                PerFormaScan: false,
                DeleteScan: false
            };
            item.DocumentTypeGroupSecurityID = 0;
            this.setDocTypes();
        }
    };

    // if they are removing a document type privilege row, we
    // just null out the permissions and unique id
    // so that it will go back into the dropdown of available types
    removeDocType = (id: number) => {
        const item = this.group.DocumentTypes.find(t => t.DocumentTypeID === id && t.DocumentPermissions);

        if (item) {
            item.DocumentPermissions = null;
            item.DocumentTypeGroupSecurityID = null;
            this.setDocTypes();
        }
    };

    setSelectableDocTypes = () => {
        this.selectableDocTypes = this.group.DocumentTypes.filter(t => !t.DocumentPermissions).map(t => {
            return {
                label: t.DocumentTypeName,
                value: t.DocumentTypeID?.toString() || ""
            };
        });
    };

    setGroupDocTypes = () => {
        this.groupDocTypes = this.group.DocumentTypes.filter(t => t.DocumentPermissions);
    };

    setDocTypes = () => {
        this.selectedDocType = "";
        this.setSelectableDocTypes();
        this.setGroupDocTypes();
    };

    //#endregion

    //#region Assigned Users

    assignUser = (id: string) => {
        const item = this.group.Users.find(u => u.UserID === Number(id) && u.UserGroupID == null);

        if (item) {
            item.UserGroupID = 0;
            this.setUsers();
        }
    };

    // if they are removing a user row, we
    // just null out the unique id
    // so that it will go back into the dropdown of available users
    removeUser = (id: number) => {
        const item = this.group.Users.find(u => u.UserID === id && u.UserGroupID != null);

        if (item) {
            item.UserGroupID = null;
            this.setUsers();
        }
    };

    setSelectableUsers = () => {
        this.selectableUsers = this.group.Users.filter(u => u.UserGroupID == null).map(u => {
            return {
                label: u.UserName + (u.Email.length > 0 ? ` (${u.Email})` : ""),
                value: u.UserID.toString()
            };
        });
    };

    setGroupUsers = () => {
        this.groupUsers = this.group.Users.filter(u => u.UserGroupID != null);
    };

    setUsers = () => {
        this.selectedUser = "";
        this.setSelectableUsers();
        this.setGroupUsers();
    };

    //#endregion

    //#region Assigned Locations

    assignLocation = (id: string) => {
        const item = this.group.Locations.find(l => l.LocationID === Number(id) && l.GroupPTPLocationID == null);

        if (item) {
            item.GroupPTPLocationID = 0;
            this.setLocations();
        }
    };

    // if they are removing a location row, we
    // just null out the unique id
    // so that it will go back into the dropdown of available locations
    removeLocation = (id: number) => {
        const item = this.group.Locations.find(l => l.LocationID === id && l.GroupPTPLocationID != null);

        if (item) {
            item.GroupPTPLocationID = null;
            this.setLocations();
        }
    };

    setSelectableLocations = () => {
        this.selectableLocations = this.group.Locations.filter(l => l.GroupPTPLocationID == null).map(l => {
            return {
                label: l.LocationName,
                value: l.LocationID.toString()
            };
        });
    };

    setGroupLocations = () => {
        this.groupLocations = this.group.Locations.filter(l => l.GroupPTPLocationID != null);
    };

    setLocations = () => {
        this.selectedLocation = "";
        this.setSelectableLocations();
        this.setGroupLocations();
    };

    //#endregion

    //#region Assigned Departments

    assignDepartment = (id: string) => {
        const item = this.group.Departments.find(d => d.DepartmentID === Number(id) && d.GroupPTPDepartmentID == null);

        if (item) {
            item.GroupPTPDepartmentID = 0;
            this.setDepartments();
        }
    };

    // if they are removing a department row, we
    // just null out the unique id
    // so that it will go back into the dropdown of available departments
    removeDepartment = (id: number) => {
        const item = this.group.Departments.find(d => d.DepartmentID === id && d.GroupPTPDepartmentID != null);

        if (item) {
            item.GroupPTPDepartmentID = null;
            this.setDepartments();
        }
    };

    setSelectableDepartments = () => {
        this.selectableDepartments = this.group.Departments.filter(d => d.GroupPTPDepartmentID == null).map(d => {
            return {
                label: d.DepartmentName,
                value: d.DepartmentID.toString()
            };
        });
    };

    setGroupDepartments = () => {
        this.groupDepartments = this.group.Departments.filter(d => d.GroupPTPDepartmentID != null);
    };

    setDepartments = () => {
        this.selectedDepartment = "";
        this.setSelectableDepartments();
        this.setGroupDepartments();
    };

    //#endregion

    //#region Assigned Steps

    assignStep = (id: string) => {
        const item = this.group.Steps.find(s => s.StepID === Number(id) && s.GroupID == null);

        if (item) {
            item.GroupID = 0;
            this.setSteps();
        }
    };

    // if they are removing a step row, we
    // just null out the group id and readonly flag
    // so that it will go back into the dropdown of available steps
    removeStep = (id: number) => {
        const item = this.group.Steps.find(s => s.StepID === id && s.GroupID != null);

        if (item) {
            item.GroupID = null;
            item.IsReadOnly = false;
            this.setSteps();
        }
    };

    setSelectableSteps = () => {
        this.selectableSteps = this.group.Steps.filter(s => s.GroupID == null).map(s => {
            const stepType = this.getStepTypeName(s.StepType);
            const stepName = s.StepName;
            return {
                label: stepType ? `${stepName} (${stepType})` : stepName,
                value: s.StepID.toString()
            };
        });
    };

    getStepTypeName(type: number): string {
        switch (type) {
            case 0:
                return "Request";
            case 1:
                return "PO";
            case 2:
                return "Report";
            case 3:
                return "Dashboard";
            default:
                return "";
        }
    }

    setGroupSteps = () => {
        this.groupSteps = this.group.Steps.filter(s => s.GroupID != null);
    };

    setSteps = () => {
        this.selectedStep = "";
        this.setSelectableSteps();
        this.setGroupSteps();
    };

    //#endregion

    //#region Assigned PtpForms

    assignPtpForm = (id: string) => {
        const item = this.group.Forms.find(f => f.FormID === Number(id) && f.GroupID == null);

        if (item) {
            item.GroupID = 0;
            this.setPTPForms();
        }
    };

    // if they are removing a ptpForm row, we
    // just null out the group id and readonly flag
    // so that it will go back into the dropdown of available ptpForms
    removePtpForm = (id: number) => {
        const item = this.group.Forms.find(f => f.FormID === id && f.GroupID != null);

        if (item) {
            item.GroupID = null;
            item.IsReadOnly = false;
            this.setPTPForms();
        }
    };

    setSelectablePtpForms = () => {
        this.selectablePtpForms = this.group.Forms.filter(f => f.GroupID == null).map(f => {
            const ptpFormType = this.getPtpFormTypeName(f.FormType);
            const ptpFormName = f.FormName;
            return {
                label: ptpFormType ? `${ptpFormName} (${ptpFormType})` : ptpFormName,
                value: f.FormID.toString()
            };
        });
    };

    getPtpFormTypeName(type: number): string {
        switch (type) {
            case 0:
                return "Request";
            case 1:
                return "PO";
            default:
                return "";
        }
    }

    setGroupPTPForms = () => {
        this.groupPtpForms = this.group.Forms.filter(f => f.GroupID != null);
    };

    setPTPForms = () => {
        this.selectedPtpForm = "";
        this.setSelectablePtpForms();
        this.setGroupPTPForms();
    };

    //#endregion

    //#region Helper Methods

    moduleIsEnabled(moduleName: string): boolean {
        return this.modules.some(m => m.Name === moduleName && m.IsEnabled);
    }

    // check for unsaved changes
    isUnsavedChanges(): boolean {
        if (!this.originalGroup) {
            return false;
        }

        return JSON.stringify(this.group) !== JSON.stringify(this.originalGroup);
    }

    // listen for browser refresh/close
    @HostListener("window:beforeunload", ["$event"])
    beforeUnload(event: BeforeUnloadEvent) {
        if (this.isUnsavedChanges()) {
            event.preventDefault();

            // Legacy/fallback: Set if the property exists (future-proofing)
            if ("returnValue" in event) {
                event.returnValue = true;
            }
        }
    }

    reset = () => {
        if (!this.isUnsavedChanges()) {
            this.message.info("There are no unsaved changes.");
            return;
        }

        this.group = JSON.parse(JSON.stringify(this.originalGroup));
        this.doSetup();
    };

    goBack = () => {
        if (this.isUnsavedChanges()) {
            if (!confirm("You have unsaved changes. Are you sure you want to leave?")) {
                return;
            }
        }

        this.router.navigateByUrl("/configure/groups");
    };

    // when various sections are saved, update the original data to mark it "clean"
    updateOriginalData = () => {
        this.originalGroup = JSON.parse(JSON.stringify(this.group));
    };

    //#endregion
}
